import { useMemo, useState } from 'react';
import { Box, Chip, CircularProgress, IconButton, Link, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchOffIcon from '@mui/icons-material/SearchOff';

import {
  useGetCatalogJurisdictionsQuery,
  useGetCatalogSetTreeQuery,
  useSearchCatalogStandardsQuery,
} from '../../standardsCatalogApi';
import type { CartItem } from '../../cartSlice';
import {
  type CatalogCheckState,
  computeCatalogCheckStates,
  indexCatalogTreeById,
} from '../../utils/catalogSelection';
import { CodeChip, EmptyState, SkeletonRows, SourceIcon, TriStateCheckbox } from '../atoms';

import { CatalogTreeRows } from './CatalogSetTree';
import type { PickedSource } from './SourceCards';

import type { CspCatalogNode, CspCatalogTreeNode } from '@/shared/types/userStandard';

type ToggleSelection = (
  node: CspCatalogNode | CspCatalogTreeNode,
  state?: CatalogCheckState
) => void;

interface CatalogSearchResultsProps {
  query: string;
  jurisdictionId?: string | null;
  subject?: string | null;
  educationLevel?: string | null;
  standardSetId?: string | null;
  includeDeprecated?: boolean;
  items: Record<string, CartItem>;
  selectedIds: ReadonlySet<string>;
  onToggle: ToggleSelection;
  onPickSource: (source: PickedSource) => void;
  onPickSubject: (node: CspCatalogNode) => void;
  onBrowseToSet: (node: CspCatalogNode) => void;
}

/** A single ranked result row. Expanding a branch hit lazily loads its set tree for inline cherry-pick. */
function SearchResultRow({
  node,
  items,
  selected,
  onToggle,
  onPickSource,
  onPickSubject,
  onBrowseToSet,
}: {
  node: CspCatalogNode;
  items: Record<string, CartItem>;
  selected: boolean;
  onToggle: ToggleSelection;
  onPickSource: (source: PickedSource) => void;
  onPickSubject: (node: CspCatalogNode) => void;
  onBrowseToSet: (node: CspCatalogNode) => void;
}) {
  const canExpand = Boolean(node.has_children);
  const selectionState: CatalogCheckState = selected ? 'on' : 'off';
  const [expanded, setExpanded] = useState(false);
  const [treeExpanded, setTreeExpanded] = useState<Set<string>>(new Set());

  const { data: setTree = [], isFetching } = useGetCatalogSetTreeQuery(
    { standardSetId: node.standard_set_id },
    { skip: !expanded || !canExpand }
  );

  // States are computed over the whole set tree so coverage from any selected ancestor is reflected,
  // even though only the hit's children are rendered inline.
  const states = useMemo(() => computeCatalogCheckStates(setTree, items), [setTree, items]);
  const hitChildren = useMemo(
    () => indexCatalogTreeById(setTree).get(node.id)?.children ?? [],
    [setTree, node.id]
  );

  const toggleTreeExpand = (id: string) =>
    setTreeExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Box
        sx={{
          pl: 1,
          pr: 2,
          py: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: canExpand ? '24px 28px minmax(0, 1fr)' : '28px minmax(0, 1fr)',
            columnGap: 0.5,
            rowGap: 0.25,
            alignItems: 'start',
          }}
        >
          {canExpand && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.25 }}>
              <IconButton
                size="small"
                onClick={() => setExpanded((prev) => !prev)}
                aria-label={expanded ? 'Collapse sub-standards' : 'Expand sub-standards'}
                sx={{
                  p: 0.25,
                }}
              >
                <ChevronRightIcon
                  fontSize="small"
                  sx={{
                    transform: expanded ? 'rotate(90deg)' : 'none',
                    transition: 'transform .15s',
                  }}
                />
              </IconButton>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <TriStateCheckbox
              state={selectionState}
              onToggle={() => onToggle(node, selectionState)}
              ariaLabel={`Select ${node.display_code}`}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', pt: 0.25 }}>
            <CodeChip code={node.display_code} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {node.description || node.display_code}
            </Typography>
          </Box>
          <Box
            sx={{
              gridColumn: canExpand ? '3' : '2',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexWrap: 'wrap',
            }}
          >
            <Link
              component="button"
              type="button"
              variant="caption"
              color="text.secondary"
              underline="hover"
              onClick={() =>
                onPickSource({
                  id: node.jurisdiction_id,
                  title: node.jurisdiction_title,
                  type: node.jurisdiction_type,
                })
              }
            >
              {node.jurisdiction_title}
            </Link>
            <Typography variant="caption" color="text.disabled">
              ›
            </Typography>
            <Link
              component="button"
              type="button"
              variant="caption"
              color="text.secondary"
              underline="hover"
              onClick={() => onPickSubject(node)}
            >
              {node.subject}
            </Link>
            <Typography variant="caption" color="text.disabled">
              ›
            </Typography>
            <Link
              component="button"
              type="button"
              variant="caption"
              color="text.secondary"
              underline="hover"
              onClick={() => onBrowseToSet(node)}
            >
              {node.standard_set_title}
            </Link>
          </Box>
        </Box>
      </Box>

      {expanded && (
        <Box sx={{ pb: 1, pl: 2 }}>
          {isFetching ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading sub-standards…
              </Typography>
            </Box>
          ) : hitChildren.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
              No sub-standards to show.
            </Typography>
          ) : (
            <CatalogTreeRows
              nodes={hitChildren}
              states={states}
              expanded={treeExpanded}
              onToggle={onToggle}
              onToggleExpand={toggleTreeExpand}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export function CatalogSearchResults({
  query,
  jurisdictionId,
  subject,
  educationLevel,
  standardSetId,
  includeDeprecated,
  items,
  selectedIds,
  onToggle,
  onPickSource,
  onPickSubject,
  onBrowseToSet,
}: CatalogSearchResultsProps) {
  const { data: results = [], isFetching } = useSearchCatalogStandardsQuery(
    { query, jurisdictionId, subject, educationLevel, standardSetId, includeDeprecated },
    { skip: !query.trim() }
  );

  // Source-suggestion lane (Decision 8a): match the query against already-loaded jurisdiction titles
  // so sources are discoverable from the standards search without polluting the per-standard vector.
  const { data: jurisdictions = [] } = useGetCatalogJurisdictionsQuery();
  const sourceSuggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return jurisdictions.filter((j) => j.title.toLowerCase().includes(q)).slice(0, 5);
  }, [jurisdictions, query]);

  const lane =
    sourceSuggestions.length > 0 ? (
      <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="overline" color="text.secondary">
          Sources
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
          {sourceSuggestions.map((j) => (
            <Chip
              key={j.id}
              icon={<SourceIcon type={j.jurisdiction_type} fontSize="small" />}
              label={j.title}
              variant="outlined"
              clickable
              onClick={() => onPickSource({ id: j.id, title: j.title, type: j.jurisdiction_type })}
            />
          ))}
        </Stack>
      </Box>
    ) : null;

  if (isFetching) {
    return (
      <Box sx={{ overflow: 'auto', height: '100%' }}>
        {lane}
        <SkeletonRows count={7} />
      </Box>
    );
  }

  if (results.length === 0) {
    return (
      <Box sx={{ overflow: 'auto', height: '100%' }}>
        {lane}
        <EmptyState
          icon={<SearchOffIcon />}
          title="No standards found"
          description={`No standards match “${query}” with the current filters.`}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ overflow: 'auto', height: '100%' }}>
      {lane}
      <Typography variant="overline" color="text.secondary" sx={{ px: 2, pt: 1, display: 'block' }}>
        {results.length} result{results.length === 1 ? '' : 's'}
      </Typography>
      {results.map((node) => (
        <SearchResultRow
          key={node.id}
          node={node}
          items={items}
          selected={selectedIds.has(node.id)}
          onToggle={onToggle}
          onPickSource={onPickSource}
          onPickSubject={onPickSubject}
          onBrowseToSet={onBrowseToSet}
        />
      ))}
    </Box>
  );
}

export default CatalogSearchResults;
